import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  createWebChatChannel,
  listWebChatChannels,
  rotateWebChatSecret,
  setWebChatStatus,
  updateWebChatOrigins,
} from './api'
import type { WebChatChannelCreated, WebChatChannelSummary, WebChatSecretRotated } from './types'

export function webChatChannelsQueryKey(tenantId: string) {
  return ['webchat', 'channels', tenantId] as const
}

/** Widgets de WebChat do tenant, para a tela de configuração. */
export function useWebChatChannels(tenantId: string | undefined) {
  return useQuery({
    queryKey: webChatChannelsQueryKey(tenantId ?? ''),
    queryFn: () => listWebChatChannels(tenantId ?? ''),
    enabled: tenantId !== undefined,
  })
}

interface CreateVariables {
  readonly name: string
  readonly allowedOrigins: readonly string[]
}

/** Cria um widget e atualiza a lista. Devolve as credenciais (secret mostrado uma vez) ao chamador. */
export function useCreateWebChatChannel(tenantId: string) {
  const queryClient = useQueryClient()
  return useMutation<WebChatChannelCreated, unknown, CreateVariables>({
    mutationFn: (variables) =>
      createWebChatChannel(tenantId, variables.name, variables.allowedOrigins),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: webChatChannelsQueryKey(tenantId) })
    },
  })
}

interface UpdateOriginsVariables {
  readonly webChatChannelId: string
  readonly allowedOrigins: readonly string[]
}

export function useUpdateWebChatOrigins(tenantId: string) {
  const queryClient = useQueryClient()
  return useMutation<WebChatChannelSummary, unknown, UpdateOriginsVariables>({
    mutationFn: (variables) =>
      updateWebChatOrigins(tenantId, variables.webChatChannelId, variables.allowedOrigins),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: webChatChannelsQueryKey(tenantId) })
    },
  })
}

export function useRotateWebChatSecret(tenantId: string) {
  const queryClient = useQueryClient()
  return useMutation<WebChatSecretRotated, unknown, { webChatChannelId: string }>({
    mutationFn: (variables) => rotateWebChatSecret(tenantId, variables.webChatChannelId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: webChatChannelsQueryKey(tenantId) })
    },
  })
}

export function useSetWebChatStatus(tenantId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    WebChatChannelSummary,
    unknown,
    { webChatChannelId: string; enabled: boolean }
  >({
    mutationFn: (variables) =>
      setWebChatStatus(tenantId, variables.webChatChannelId, variables.enabled),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: webChatChannelsQueryKey(tenantId) })
    },
  })
}
